import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, buildSrcSet, getImageSizes } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function LogosSection({ block = {}, mediaFolders = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const _allImages = content.folder_id ? (mediaFolders[content.folder_id] || []) : [];
    const _sel = content.selected_file_ids;
    const folderImages = _sel?.length > 0 ? _allImages.filter(f => _sel.includes(f.id)) : _allImages;
    const logos = folderImages;
    const anim = getAnimation(config, visible);

    if (logos.length === 0) return null;

    const variant = config.variant || 'static';
    // Fixed container size (w × h) with fallback to old logo_size presets
    const oldSizeMap = { small: 32, medium: 48, large: 80 };
    const logoH = config.logo_height || oldSizeMap[config.logo_size] || 48;
    const logoW = config.logo_width || 120;
    const logoFit = config.logo_fit || 'contain';
    const sizeStyle = { width: logoW, height: logoH };
    const imgClass = `w-full h-full object-${logoFit}`;

    if (variant === 'marquee' || variant === 'marquee-fade') {
        const uid = `lm-${block.id || 'x'}`;
        const perSet = Math.max(1, Math.ceil(12 / logos.length));
        const oneSet = Array.from({ length: perSet }, () => logos).flat();
        const speed = config.marquee_speed || 'normal';
        const speedDuration = speed === 'slow' ? Math.max(logos.length * 6, 20) : speed === 'fast' ? Math.max(logos.length * 2, 8) : Math.max(logos.length * 4, 12);
        const fadeEdges = variant === 'marquee-fade';
        const fadeMask = fadeEdges ? { WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)', maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' } : {};
        const trackHeight = logoH + 16; // logo height + vertical padding
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={anim}>
                    {content.title && (
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                        </div>
                    )}
                    {/* Track wrapper: fixed height + overflow hidden isolates marquee from layout */}
                    <div style={{ position: 'relative', height: trackHeight, overflow: 'hidden', ...fadeMask }}>
                        <div className={`${uid}-track`}>
                            <div className={`${uid}-set`}>
                                {oneSet.map((logo, i) => (
                                    <div key={i} className="shrink-0 flex items-center justify-center px-4 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-300" style={{ ...sizeStyle, opacity: (config.logo_opacity || 60) / 100 }}>
                                        <img src={logo.image_url} srcSet={buildSrcSet(logo)} sizes={`${logoW}px`} alt={logo.title || ''} className={imgClass} loading="lazy" decoding="async" />
                                    </div>
                                ))}
                            </div>
                            <div className={`${uid}-set`} aria-hidden="true">
                                {oneSet.map((logo, i) => (
                                    <div key={i} className="shrink-0 flex items-center justify-center px-4 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-300" style={{ ...sizeStyle, opacity: (config.logo_opacity || 60) / 100 }}>
                                        <img src={logo.image_url} srcSet={buildSrcSet(logo)} sizes={`${logoW}px`} alt={logo.title || ''} className={imgClass} loading="lazy" decoding="async" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                <style>{`
                    .${uid}-track {
                        position: absolute;
                        left: 0;
                        right: 0;
                        top: 0;
                        display: flex;
                    }
                    .${uid}-set {
                        display: flex;
                        align-items: center;
                        gap: 3rem;
                        flex-shrink: 0;
                        padding-right: 3rem;
                        animation: ${uid}-scroll ${speedDuration}s linear infinite;
                    }
                    .${uid}-track:hover .${uid}-set {
                        animation-play-state: paused;
                    }
                    @keyframes ${uid}-scroll {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-100%); }
                    }
                `}</style>
            </section>
        );
    }

    // inline (inteligente360 style - small logos 30px, draggable row, centered)
    if (variant === 'inline') {
        return (
            <section className="relative" style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <div ref={ref} className={`max-w-[1400px] mx-auto px-[5%] py-5 ${anim}`}>
                    {content.title && (
                        <p className="text-center text-xs font-medium uppercase tracking-widest mb-4" style={{ color: s.textSub }}>{content.title}</p>
                    )}
                    <div className="logos-inline-scroll flex items-center justify-center gap-[25px] overflow-x-auto pb-2 cursor-grab active:cursor-grabbing" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {logos.map((logo, i) => (
                            <div key={logo.id} className={`flex items-center justify-center shrink-0 transition-all duration-300 ${visible ? 'hover:opacity-100' : 'opacity-0'}`}
                                style={{ ...sizeStyle, transitionDelay: visible ? `${i * 60}ms` : '0ms', ...(visible ? { opacity: (config.logo_opacity || 60) / 100 } : {}) }}>
                                <img src={logo.image_url} srcSet={buildSrcSet(logo)} sizes={`${logoW}px`} alt={logo.title || ''} className={imgClass} loading="lazy" decoding="async" />
                            </div>
                        ))}
                    </div>
                    <style>{`.logos-inline-scroll::-webkit-scrollbar { display: none; }`}</style>
                </div>
            </section>
        );
    }

    // glassmorphism
    if (variant === 'glassmorphism') {
        return (
            <section className={`${s.padClass} relative`} style={{ backgroundColor: '#0f172a', ...(s.bgStyle.background ? s.bgStyle : {}) }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config} />
                <div ref={ref} className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="relative rounded-2xl p-8 md:p-12" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: `0 0 60px -15px ${s.accent}33` }}>
                        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-10">
                            {logos.map((logo, i) => (
                                <div key={logo.id} className={`flex items-center justify-center grayscale hover:grayscale-0 hover:scale-110 transition-all duration-500 ${visible ? 'translate-y-0 opacity-60' : 'opacity-0 translate-y-4'}`}
                                    style={{ ...sizeStyle, transitionDelay: visible ? `${i * 80}ms` : '0ms' }}>
                                    {logo.link ? (
                                        <a href={logo.link} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                                            <img src={logo.image_url} srcSet={buildSrcSet(logo)} sizes={`${logoW}px`} alt={logo.title || ''} className={`${imgClass} brightness-0 invert hover:brightness-100 hover:invert-0 transition-all duration-500`} loading="lazy" decoding="async" />
                                        </a>
                                    ) : (
                                        <img src={logo.image_url} srcSet={buildSrcSet(logo)} sizes={`${logoW}px`} alt={logo.title || ''} className={`${imgClass} brightness-0 invert hover:brightness-100 hover:invert-0 transition-all duration-500`} loading="lazy" decoding="async" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config} />
            </section>
        );
    }

    // hover-reveal
    if (variant === 'hover-reveal') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config} />
                <div ref={ref} className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                        {logos.map((logo, i) => (
                            <div key={logo.id} className={`group relative flex flex-col items-center justify-center rounded-xl border p-6 transition-all duration-500 hover:shadow-lg ${visible ? 'translate-y-0' : 'opacity-0 translate-y-4'}`}
                                style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', backgroundColor: s.dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', transitionDelay: visible ? `${i * 70}ms` : '0ms' }}>
                                <div className="flex items-center justify-center grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-400" style={sizeStyle}>
                                    {logo.link ? (
                                        <a href={logo.link} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                                            <img src={logo.image_url} srcSet={buildSrcSet(logo)} sizes={`${logoW}px`} alt={logo.title || ''} className={imgClass} loading="lazy" decoding="async" style={{ opacity: (config.logo_opacity || 60) / 100 }} />
                                        </a>
                                    ) : (
                                        <img src={logo.image_url} srcSet={buildSrcSet(logo)} sizes={`${logoW}px`} alt={logo.title || ''} className={imgClass} loading="lazy" decoding="async" style={{ opacity: (config.logo_opacity || 60) / 100 }} />
                                    )}
                                </div>
                                {logo.title && (
                                    <div className="absolute bottom-0 left-0 right-0 overflow-hidden">
                                        <div className="translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out text-center pb-2 pt-1">
                                            <span className="text-xs font-medium tracking-wide" style={{ color: s.accent }}>{logo.title}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config} />
            </section>
        );
    }

    // static (default)
    return (
        <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
                    {logos.map((logo, i) => (
                        <div key={logo.id} className={`flex items-center justify-center px-4 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500 ${visible ? 'translate-y-0' : 'opacity-0 translate-y-4'}`}
                            style={{ ...sizeStyle, transitionDelay: visible ? `${i * 100}ms` : '0ms', ...(visible ? { opacity: (config.logo_opacity || 60) / 100 } : {}) }}>
                            <img src={logo.image_url} srcSet={buildSrcSet(logo)} sizes={`${logoW}px`} alt={logo.title || ''} className={imgClass} loading="lazy" decoding="async" />
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
